import { forceUpdate, h, Host } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import { getLanguageOptions } from "./utils";
const baseClass = "uxc-app-language-selector";
export class AppLanguageSelector {
    constructor() {
        this.i18nSubscriberId = "";
        /**
         * If `passive` is set, the language is no longer bound to the `@trumpf/ux.i18n` frontend API.
         * Only set this flag if you want to take full control of language management.
         *
         * Use the `language` prop and the `uxLanguageChange` event to control the component state.
         */
        this.passive = false;
        this.handleI18n = ({ language }) => {
            if (this.passive) {
                this.updateOptions();
                return;
            }
            this.language = language;
            forceUpdate(this);
        };
        this.handleChange = (event) => {
            const lng = event.target.value;
            if (!lng) {
                return;
            }
            if (this.languageChangeEvent.emit(lng).defaultPrevented) {
                forceUpdate(this);
                return;
            }
            if (this.passive) {
                this.language = lng;
                return;
            }
            i18nProvider.changeLanguage(lng).then(() => {
                this.language = i18nProvider.language;
            });
        };
    }
    /** Update local options */
    updateOptions() {
        const options = getLanguageOptions(this.languages).map((option) => (Object.assign(Object.assign({}, option), { selected: option.value === this.language })));
        const selectedOption = options.find((option) => !!option.selected);
        if (this.language && !selectedOption) {
            options.unshift(getLanguageOptions(this.language)[0]);
        }
        this.options = options;
    }
    componentWillLoad() {
        this.language = this.passive ? this.language : i18nProvider.language;
        this.updateOptions();
    }
    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(this.handleI18n);
    }
    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }
    componentDidRender() {
        this.element.querySelectorAll("option").forEach((option) => {
            option.selected = option.value === this.language;
        });
    }
    render() {
        const { handleChange, language, options } = this;
        const { t } = i18nProvider;
        const label = this.label || t(`userAccount.language.label`);
        return (h(Host, { key: '4306ef553506fe348c007e4c312c3379f2cf901f', class: baseClass }, h("uic-dropdown", { key: '23902ed3a11afa4b2ecc3aac4a73e31ef04654de', onUicChange: handleChange, label: label, value: language, placeholder: this.placeholder || t(`userAccount.language.select`), hoist: true }, options.map((option) => (h("uic-list-item", { key: `${option.value}-${option.name}`, value: option.value, selected: option.selected, disabled: option.disabled }, option.name))))));
    }
    static get is() { return "uxc-app-language-selector"; }
    static get originalStyleUrls() {
        return {
            "$": ["app-language-selector.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["app-language-selector.css"]
        };
    }
    static get properties() {
        return {
            "passive": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If `passive` is set, the language is no longer bound to the `@trumpf/ux.i18n` frontend API.\nOnly set this flag if you want to take full control of language management.\n\nUse the `language` prop and the `uxLanguageChange` event to control the component state."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "attribute": "passive",
                "defaultValue": "false"
            },
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Custom label text used instead of i18n translation"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "attribute": "label"
            },
            "placeholder": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Custom placeholder text used instead of i18n translation"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "attribute": "placeholder"
            },
            "language": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The current language (automatically detected by default)\n\nLanguage tags must comply with the RFC 2070 specification."
                },
                "getter": false,
                "setter": false,
                "reflect": true,
                "attribute": "language"
            },
            "languages": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | string[] | LanguageOption[]",
                    "resolved": "LanguageOption[] | string | string[]",
                    "references": {
                        "LanguageOption": {
                            "location": "import",
                            "path": "./interface",
                            "id": "src/components/app-language-selector/interface.ts::LanguageOption"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The languages the user can choose from (e.g. `\"de,en,en-US,fr\"`)\n\nLanguage tags must comply with the RFC 2070 specification."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "attribute": "languages"
            }
        };
    }
    static get states() {
        return {
            "options": {}
        };
    }
    static get events() {
        return [{
                "method": "languageChangeEvent",
                "name": "uxLanguageChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the user selects an option"
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "language",
                "methodName": "updateOptions"
            }, {
                "propName": "languages",
                "methodName": "updateOptions"
            }];
    }
}
//# sourceMappingURL=app-language-selector.js.map
